import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calculator, MessageCircle, PackageSearch } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";

const WA = "https://wa.me/919783807838";

const SERVICE_META = {
  domestic: {
    img: "/service-pages/svc_domestic.jpeg",
    tagline: "Doorstep pickup · pan-India delivery · express & economy · live tracking",
    cta: { label: "Book a Domestic Shipment", to: "/login" },
  },
  international: {
    img: "/service-pages/svc_international.jpeg",
    tagline: "220+ destinations · DHL / FedEx / UPS / Aramex network · customs assistance · door-to-door",
    cta: { label: "Get International Quote", to: "/login" },
  },
  ecommerce: {
    img: "/service-pages/svc_ecommerce.jpeg",
    tagline: "Bulk shipping · COD · NDR/RTO management · seller dashboard & reports",
    cta: { label: "Start Shipping", to: "/register" },
  },
  business: {
    img: "/service-pages/svc_business.jpeg",
    tagline: "Recurring pickups · bulk shipments · dedicated account management · custom pricing",
    cta: { label: "Open Business Account", to: "/register" },
  },
  nri: {
    img: "/service-pages/svc_nri.jpeg",
    tagline: "Gifts, clothing & permitted food · professional multi-layer packing · full customs guidance",
    cta: { label: "Plan an NRI Shipment", to: WA },
  },
};

export default function CmsPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [contact, setContact] = useState(null);
  const [missing, setMissing] = useState(false);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    setPage(null);
    setMissing(false);
    api.get(`/public/page/${slug}`).then((r) => setPage(r.data)).catch(() => setMissing(true));
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
    if (slug === "news") api.get("/public/blog").then((r) => setPosts(r.data)).catch(() => {});
    else setPosts([]);
    window.scrollTo(0, 0);
  }, [slug]);

  const meta = SERVICE_META[slug];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cms-page">
      <Navbar />
      {meta ? (
        <div className="relative overflow-hidden bg-navy">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-16 lg:grid-cols-2">
            <div>
              <Link to="/" data-testid="cms-back-home"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-300 transition-colors hover:text-saffron">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
              </Link>
              <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="cms-page-title">
                {page ? page.title : "…"}
              </h1>
              <div className="mt-5 h-1 w-28 rounded-full bg-gradient-to-r from-saffron via-white to-green-600" />
              <p className="mt-5 max-w-lg text-sm leading-relaxed text-slate-300" data-testid="cms-service-tagline">{meta.tagline}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={meta.cta.to} data-testid="cms-service-cta"
                      className="group inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-all hover:bg-saffron-hover">
                  {meta.cta.label} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link to="/rate-calculator" data-testid="cms-service-rate-btn"
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold text-white transition-colors hover:border-saffron hover:text-saffron">
                  <Calculator className="h-4 w-4" /> Calculate Rate
                </Link>
                <Link to="/track" data-testid="cms-service-track-btn"
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold text-white transition-colors hover:border-saffron hover:text-saffron">
                  <PackageSearch className="h-4 w-4" /> Track Shipment
                </Link>
              </div>
            </div>
            <div className="relative" data-testid="cms-service-hero">
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-saffron/30 via-transparent to-green-600/20 blur-xl" />
              <img src={meta.img} alt={page?.title || slug}
                   className="relative w-full rounded-2xl border border-white/10 shadow-2xl" />
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-navy py-14">
          <div className="mx-auto max-w-3xl px-6">
            <Link to="/" data-testid="cms-back-home"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-300 transition-colors hover:text-saffron">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
            </Link>
            <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="cms-page-title">
              {missing ? "Page Not Found" : page ? page.title : "Loading…"}
            </h1>
            <div className="mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-saffron via-white to-green-600" />
          </div>
        </div>
      )}

      <div className={`mx-auto px-6 py-12 ${meta ? "max-w-4xl" : "max-w-3xl"}`}>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          {missing ? (
            <p className="text-sm text-slate-500">This page is not available. Please check the link or return to the homepage.</p>
          ) : (
            <p className="whitespace-pre-line text-sm leading-7 text-slate-700" data-testid="cms-page-body">
              {page ? page.body : "Loading content…"}
            </p>
          )}
        </div>
      </div>

      {slug === "news" && posts.length > 0 && (
        <div className="mx-auto max-w-4xl px-6 pb-12" data-testid="blog-list">
          <h3 className="mb-4 font-heading text-xl font-bold text-navy">Latest Articles</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.id}`} data-testid={`blog-card-${p.id}`}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
                {p.image_url ? <img src={p.image_url} alt="" className="mb-3 h-36 w-full rounded-xl object-cover" /> : null}
                <p className="text-[10px] font-bold uppercase tracking-wider text-saffron">{p.category || "General"}</p>
                <p className="mt-1 font-heading text-base font-bold text-navy">{p.title}</p>
                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.body}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {meta && (
        <div className="mx-auto max-w-4xl px-6 pb-16">
          <div className="relative overflow-hidden rounded-2xl bg-navy p-10 text-center">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-saffron via-white to-green-600" />
            <h3 className="font-heading text-2xl font-extrabold text-white">Ready to ship with INDIAFAST?</h3>
            <p className="mt-2 text-sm text-slate-300">Delivering Promises, Connecting Worldwide — +91 97838 07838</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/register" data-testid="cms-bottom-cta"
                    className="rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">Create Free Account</Link>
              <a href={WA} target="_blank" rel="noreferrer" data-testid="cms-bottom-wa"
                 className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold text-white transition-colors hover:border-emerald-400 hover:text-emerald-400">
                <MessageCircle className="h-4 w-4" /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      )}
      <Footer contact={contact} />
    </div>
  );
}
